import { useState, useEffect } from "react";
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameDay,
  isSameMonth,
  addMonths,
  subMonths,
  startOfWeek,
  endOfWeek,
} from "date-fns";
import { ChevronLeft, ChevronRight, CalendarDays } from "lucide-react";
import { Meeting } from "@/services/api";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { clientApi, meetingApi } from "@/services/api";
import { Trash2 } from "lucide-react";
import { toast } from "@/hooks/use-toast";

type CalendarProps = {
  meetings: Meeting[];
  onSelectMeeting: (meeting: Meeting) => void;
  onMeetingDeleted?: (meetingId: string) => void;
};

export function Calendar({ meetings, onSelectMeeting, onMeetingDeleted }: CalendarProps) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [hoveredDay, setHoveredDay] = useState<Date | null>(null);
  const [clientNames, setClientNames] = useState<Record<string, string>>({});
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; meeting: Meeting | null }>({
    open: false,
    meeting: null
  });
  const [deletingMeeting, setDeletingMeeting] = useState(false);

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(monthStart);
  const calendarStart = startOfWeek(monthStart);
  const calendarEnd = endOfWeek(monthEnd);
  const calendarDays = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  // Fetch client names for all meetings
  useEffect(() => {
    const fetchClientNames = async () => {
      const clientIds = [...new Set(meetings.map(m => m.client).filter(Boolean))];

      console.log("🔍 Meeting client IDs:", clientIds);
      console.log("🔍 Meetings data:", meetings);

      try {
        const clients = await clientApi.getAll();
        const names: Record<string, string> = {};

        console.log("📦 Clients from API:", clients);

        clients.forEach(client => {
          const id = String(client._id);
          if (clientIds.includes(id)) {
            names[id] = client.name;
          }
        });

        console.log("✅ Matched client names:", names);
        setClientNames(names);
      } catch (error) {
        console.error("Error fetching client names:", error);
      }
    };

    if (meetings.length > 0) {
      fetchClientNames();
    }
  }, [meetings]);

  const nextMonth = () => {
    setCurrentMonth(addMonths(currentMonth, 1));
  };

  const prevMonth = () => {
    setCurrentMonth(subMonths(currentMonth, 1));
  };

  const getMeetingsForDay = (day: Date) => {
    return meetings.filter((meeting) => {
      const meetingDate = new Date(meeting.startTime);
      return isSameDay(meetingDate, day);
    });
  };

  const getClientName = (clientId: string, meeting?: Meeting) => {
    const normalizedId = String(clientId || "");
    
    // First try to get clientName from the meeting object (populated by Dashboard)
    if (meeting && meeting.clientName) {
      return meeting.clientName;
    }
    
    // Then try to get from the clientNames lookup
    if (clientNames[normalizedId]) {
      return clientNames[normalizedId];
    }
    
    // If still not found, try to find in the current meetings array
    const meetingWithClient = meetings.find(m => 
      String(m.client) === normalizedId && m.clientName
    );
    
    if (meetingWithClient && meetingWithClient.clientName) {
      return meetingWithClient.clientName;
    }
    
    console.log(`❌ Could not find client name for ID: ${normalizedId}`);
    console.log(`❌ Available client names:`, clientNames);
    console.log(`❌ Meeting data:`, meeting);
    
    return "Unknown Client";
  };

  const getTypeColor = (type: Meeting["type"]) => {
    switch (type) {
      case "initial":
        return "bg-nutrition-green-500";
      case "follow-up":
        return "bg-nutrition-orange-400";
      case "assessment":
        return "bg-blue-500";
      default:
        return "bg-gray-400";
    }
  };

  const isToday = (day: Date) => isSameDay(day, new Date());

  const handleDeleteMeeting = async () => {
    if (!deleteDialog.meeting) return;
    
    setDeletingMeeting(true);
    try {
      await meetingApi.delete(deleteDialog.meeting._id!);
      
      // Notify parent component about the deleted meeting
      if (onMeetingDeleted) {
        onMeetingDeleted(deleteDialog.meeting._id!);
      }
      
      toast({
        title: "Meeting Deleted",
        description: "The meeting has been successfully deleted.",
        variant: "default",
      });
      
      setDeleteDialog({ open: false, meeting: null });
    } catch (error) {
      console.error("Error deleting meeting:", error);
      toast({
        title: "Error",
        description: "Failed to delete meeting. Please try again.",
        variant: "destructive",
      });
    } finally {
      setDeletingMeeting(false);
    }
  };

  const openDeleteDialog = (meeting: Meeting) => {
    setDeleteDialog({ open: true, meeting });
  };

  return (
    <div className="nutrition-card overflow-hidden">
      <div className="flex justify-between items-start sm:items-center flex-col sm:flex-row gap-3 sm:gap-0 mb-4 sm:mb-6">
        <h2 className="text-lg sm:text-xl font-semibold flex items-center">
          <CalendarDays className="mr-2 h-5 w-5 text-nutrition-green-500" />
          {format(currentMonth, "MMMM yyyy")}
        </h2>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="icon"
            onClick={prevMonth}
            className="h-7 w-7 sm:h-8 sm:w-8 rounded-full hover:bg-nutrition-gray-200"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            onClick={() => setCurrentMonth(new Date())}
            className="h-7 sm:h-8 px-2 text-[10px] sm:text-xs hover:bg-nutrition-gray-200"
          >
            Today
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={nextMonth}
            className="h-7 w-7 sm:h-8 sm:w-8 rounded-full hover:bg-nutrition-gray-200"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="overflow-x-auto sm:overflow-visible">
        <div className="grid grid-cols-7 gap-1 mb-2 min-w-[700px] sm:min-w-0">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
            <div key={day} className="text-center text-xs sm:text-sm font-medium py-1.5 sm:py-2 text-nutrition-gray-400">
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1 min-w-[700px] sm:min-w-0">
          {calendarDays.map((day, i) => {
          const dayMeetings = getMeetingsForDay(day);
          const isCurrentMonth = isSameMonth(day, currentMonth);
          const hasManyMeetings = dayMeetings.length > 3;
          const todayClass = isToday(day) ? "ring-2 ring-nutrition-green-400 ring-inset" : "";
          const isHovered = hoveredDay && isSameDay(day, hoveredDay);

            return (
              <div
                key={i}
                className={cn(
                  "min-h-[80px] sm:min-h-[100px] border rounded-lg p-1 sm:p-1 transition-all duration-200",
                  isCurrentMonth ? "bg-white" : "bg-nutrition-gray-50 text-nutrition-gray-400",
                  todayClass,
                  isHovered && "bg-nutrition-gray-100",
                  "hover:shadow-md"
                )}
                onMouseEnter={() => setHoveredDay(day)}
                onMouseLeave={() => setHoveredDay(null)}
              >
                <div
                  className={cn(
                    "text-right text-[11px] sm:text-sm mb-1 font-medium p-0.5 sm:p-1 rounded-full w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center ml-auto",
                    isToday(day) && "bg-nutrition-green-500 text-white"
                  )}
                >
                  {format(day, "d")}
                </div>

                {dayMeetings.length > 0 ? (
                  <ScrollArea className="h-[68px] sm:h-[85px] pr-1 sm:pr-2">
                    <div className="space-y-1">
                      {dayMeetings.map((meeting) => (
                        <div
                          key={meeting._id}
                          onClick={() => onSelectMeeting(meeting)}
                          className={cn(
                            "cursor-pointer hover:bg-nutrition-gray-100 rounded-md p-1.5 group",
                            "text-[11px] sm:text-xs font-medium truncate transition-all duration-200",
                            "border-l-2",
                            `border-l-${getTypeColor(meeting.type).replace("bg-", "")}`
                          )}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center">
                              <div
                                className={cn(
                                  "h-2 w-2 rounded-full mr-1",
                                  getTypeColor(meeting.type)
                                )}
                              ></div>
                              <div className="font-bold text-nutrition-gray-600">
                                {format(new Date(meeting.startTime), "h:mm a")}
                              </div>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0 hover:bg-red-100 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                              onClick={(e) => {
                                e.stopPropagation();
                                openDeleteDialog(meeting);
                              }}
                              title="Delete meeting"
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                          <div className="truncate pl-3 text-nutrition-gray-700">
                            {getClientName(meeting.client || "", meeting)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                ) : (
                  <div className="h-[68px] sm:h-[85px] flex items-center justify-center text-[11px] sm:text-xs text-nutrition-gray-400">
                    {isCurrentMonth ? "No meetings" : ""}
                  </div>
                )}

                {hasManyMeetings && (
                  <div
                    className={cn(
                      "text-[11px] sm:text-xs text-center mt-1 font-medium px-1 py-0.5 rounded-md bg-gradient-to-r",
                      dayMeetings.length > 5
                        ? "from-nutrition-orange-100 to-nutrition-orange-200 text-nutrition-orange-600"
                        : "from-nutrition-green-100 to-nutrition-green-200 text-nutrition-green-600"
                    )}
                  >
                    {dayMeetings.length} meetings
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Delete Meeting Confirmation Dialog */}
      {deleteDialog.open && deleteDialog.meeting && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Delete Meeting</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete the meeting "{deleteDialog.meeting.title}" on{" "}
              {format(new Date(deleteDialog.meeting.startTime), "MMMM d, yyyy 'at' h:mm a")}?
            </p>
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={() => setDeleteDialog({ open: false, meeting: null })}
                disabled={deletingMeeting}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeleteMeeting}
                disabled={deletingMeeting}
              >
                {deletingMeeting ? "Deleting..." : "Delete Meeting"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
